import { isValidIRDNumber } from "@fnzc/nz-ird-validator";
import { Box, Button, Grid } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import { useCallback, useState } from "react";
import { RouteComponentProps } from "react-router";
import * as yup from "yup";
import { AdviceDelivery, AdviceStatuses, AdviceType, OnboardDelivery, Product } from "../../../../api";
import { Client, ScreenProps, titles, yesNoOptions } from "../../../../common/types";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { saveClient } from "../../../../store/adviser";
import EditPreviewDisclosure from "../../../application/EditPreviewDisclosure";
import FormikWrapper from "../../../containers/FormikWrapper";
import { Colors, DialogBox, FormLabel, ProductIcon, SectionTitleText, SubtitleText, TitleText } from "../../../controls/helpers";
import { Checkbox, NumberTextInput, Select, TextInput } from "../../../controls/inputs";

enum DialogMode {
  None,
  Disclosure,
  Prepopulated,
}

const cleanValues = (values: Client) => ({
  ...(values.prepopulated
    ? values
    : {
        clientId: values.clientId,
        salutation: values.salutation,
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        email: values.email,
        mobile: values.mobile,
        irdNumber: values.irdNumber,
        adviceStatus: values.adviceStatus,
        adviceType: values.adviceType,
        adviceDelivery: values.adviceDelivery,
        useNZFundsDisclosureTemplate: values.useNZFundsDisclosureTemplate,
        prepopulated: values.prepopulated,
        onboardDelivery: values.onboardDelivery,
        productOptions: values.productOptions,
        products: values.products,
      }),
  kiwiSaverApplication: values.products?.includes(Product.KiwiSaver) && !values.prepopulated ? { active: true } : values.kiwiSaverApplication,
  wealthBuilderApplications: values.products?.includes(Product.WealthBuilder) && !values.prepopulated ? [{ active: true }] : [...values.wealthBuilderApplications!],
  incomeGeneratorApplications: values.products?.includes(Product.IncomeGenerator) && !values.prepopulated ? [{ active: true }] : [...values.incomeGeneratorApplications!],
});

const ApplicationDetails = ({ history, nextRoute, prevRoute }: RouteComponentProps & ScreenProps) => {
  const { client, disclosureTemplate } = useAppSelector((state) => state.adviser);
  const [dialogMode, setDialogMode] = useState(DialogMode.None);
  const dispatch = useAppDispatch();

  const initialValues = useCallback(
    () => ({
      salutation: "",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      mobile: "",
      irdNumber: "",
      adviceType: AdviceType.Limited,
      onboardDelivery: OnboardDelivery.Browser,
      useNZFundsDisclosureTemplate: false,
      prepopulated: false,
      ...client,
      consented: client?.prepopulated || false,
    }),
    [client]
  );

  const handleSubmit = useCallback(
    (values: Client) => {
      if (values.useNZFundsDisclosureTemplate && !disclosureTemplate) {
        setDialogMode(DialogMode.Disclosure);
        return;
      }

      if (!values.prepopulated && client?.prepopulated && dialogMode === DialogMode.None) {
        setDialogMode(DialogMode.Prepopulated);
        return;
      }

      const _values = cleanValues(values);
      dispatch(saveClient(_values));
      nextRoute(_values);
    },
    [client?.prepopulated, dialogMode, disclosureTemplate, dispatch, nextRoute]
  );

  const goToDisclosureTemplate = useCallback(
    (values: Client) => {
      dispatch(saveClient(cleanValues(values)));
      history.push("/adviser/disclosure-template");
    },
    [dispatch, history]
  );

  const handleDialog = useCallback(
    (values: Client) => {
      switch (dialogMode) {
        case DialogMode.Disclosure:
          goToDisclosureTemplate(values);
          break;
        case DialogMode.Prepopulated:
          handleSubmit(values);
          break;
      }
    },
    [dialogMode, goToDisclosureTemplate, handleSubmit]
  );

  return (
    <FormikWrapper
      initialValues={initialValues()}
      onSubmit={handleSubmit}
      validationSchema={yup.object({
        salutation: yup.string(),
        firstName: yup.string().required("First name is required"),
        middleName: yup.string(),
        lastName: yup.string().required("Last name is required"),
        email: yup.string().email("Email is invalid").required("Email is required"),
        mobile: yup.string(),
        irdNumber: yup.string().test("IRD valid", "IRD number is invalid", (value) => !value || isValidIRDNumber(value)),
        adviceType: yup.mixed<AdviceType>().required("An option is required"),
        onboardDelivery: yup.mixed<OnboardDelivery>().required("An option is required"),
        useNZFundsDisclosureTemplate: yup.boolean().required("An option is required"),
        prepopulated: yup.boolean().required("An option is required"),
      })}
    >
      {({ setFieldValue, values }) => (
        <>
          <Box>
            <TitleText hideMarginBottom>Client</TitleText>
            <SubtitleText hideMarginBottom>
              <Grid container justifyContent="space-between">
                <Grid item style={{ paddingTop: 3 }}>
                  {(!!values.firstName || !!values.lastName) && (
                    <>
                      <span style={{ color: Colors.Black }}>
                        {values.firstName} {values.lastName}{" "}
                      </span>
                      &emsp;
                    </>
                  )}
                  {values.adviceStatus === AdviceStatuses.Skipped ? "Product onboarding" : "Savings Advice Tool"}
                </Grid>
                <Grid item>
                  {!!values.kiwiSaverApplication && <ProductIcon product={Product.KiwiSaver} highlighted />}
                  {!!values.wealthBuilderApplications?.length && <ProductIcon product={Product.WealthBuilder} highlighted />}
                  {!!values.incomeGeneratorApplications?.length && <ProductIcon product={Product.IncomeGenerator} highlighted />}
                </Grid>
              </Grid>
            </SubtitleText>
          </Box>
          <Box mb={3}>
            <SectionTitleText>Personal information</SectionTitleText>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SubtitleText hideMarginBottom>Enter client name as shown on NZ Driver Licence or NZ Passport.</SubtitleText>
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <Select name="salutation" label="Title" options={titles} />
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <TextInput
                  placeholder="First name as per client's ID"
                  label={
                    <>
                      First name<span style={{ color: Colors.Red }}>*</span>
                    </>
                  }
                  name="firstName"
                />
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <TextInput placeholder="Middle name as per client's ID" label="Middle name(s)" name="middleName" />
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <TextInput
                  placeholder="Last name as per client's ID"
                  label={
                    <>
                      Last name<span style={{ color: Colors.Red }}>*</span>
                    </>
                  }
                  name="lastName"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextInput
                  label={
                    <>
                      Email<span style={{ color: Colors.Red }}>*</span>
                    </>
                  }
                  name="email"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <NumberTextInput label="Mobile" name="mobile" mask="999999999999" setStringValue />
              </Grid>
              <Grid item sm={6} xs={12}>
                <NumberTextInput label="IRD number" name="irdNumber" mask="999999999" setStringValue />
              </Grid>
            </Grid>
          </Box>
          <Box mb={3}>
            <SectionTitleText>Application</SectionTitleText>
            <Grid container spacing={3}>
              {values.adviceDelivery === AdviceDelivery.None && (
                <Grid item xs={12}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item sm={8} xs={12}>
                      <FormLabel
                        label="Advice type"
                        tooltip={
                          <>
                            <span>
                              <b>Limited advice</b> (also known as 'class advice') is a recommendation that is not comprehensive advice. It takes into account the pre-defined characteristics that come
                              with a group of people (e.g. gender, age and risk profile), rather than information specific to a particular person (e.g. financial situation or goals).
                            </span>
                            <br />
                            <br />
                            <span>
                              <b>Information only</b> is not advice. It is providing details about the financial product (e.g. features and eligibility).
                            </span>
                            <br />
                            <br />
                            <span>
                              <b>Comprehensive advice</b>, also known as 'personalised advice', requires a written financial plan that takes into account a person's financial situation or goals (e.g.
                              assets, expenses and savings goals).
                            </span>
                          </>
                        }
                      />
                    </Grid>
                    <Grid item sm={4} xs={12}>
                      <Select
                        name="adviceType"
                        options={[
                          { label: "Limited advice", value: AdviceType.Limited },
                          { label: "Information only", value: AdviceType.Information },
                          { label: "Comprehensive advice", value: AdviceType.Comprehensive },
                        ]}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <EditPreviewDisclosure onEditClick={() => goToDisclosureTemplate(values)} />
              {!(values.products?.length === 1 && values.products.includes(Product.KiwiSaver)) && (
                <>
                  <Grid item xs={12}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item sm={8} xs={12}>
                        <FormLabel label="Pre-populate the client's details?" />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <Select name="prepopulated" options={yesNoOptions} onChange={(selected) => !selected && setFieldValue("consented", false)} />
                      </Grid>
                    </Grid>
                  </Grid>
                  {values.prepopulated && (
                    <Grid item xs={12}>
                      <Checkbox
                        name="consented"
                        labelPlacement="start"
                        label={
                          <>
                            Please confirm that the client has agreed for you to pre-populate their details, that this is a suitable process for the client and is being completed in conjunction with
                            the client.
                            <br />
                            Please advise the client to check all details carefully when they complete the application and make any changes that are required.
                          </>
                        }
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Box>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item>
              {!!prevRoute && (
                <Button
                  variant="contained"
                  type="button"
                  onClick={() => {
                    dispatch(saveClient(values));
                    prevRoute(values);
                  }}
                >
                  Previous
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button variant="contained" type="submit" color="primary" disabled={values.prepopulated && !values.consented}>
                Next
              </Button>
            </Grid>
          </Grid>
          <DialogBox icon={<CancelIcon htmlColor={Colors.Red} />} open={dialogMode !== DialogMode.None} ok={() => handleDialog(values)} cancel={() => setDialogMode(DialogMode.None)}>
            <SubtitleText black hideMarginBottom>
              {dialogMode === DialogMode.Disclosure && "The NZ Funds disclosure statement template needs to be completed. Would you like to go there now?"}
              {dialogMode === DialogMode.Prepopulated && (
                <>
                  You selected not to pre-populate the client's onboarding details - this will clear any existing pre-populated details.
                  <br />
                  <br />
                  Do you want to continue?
                </>
              )}
            </SubtitleText>
          </DialogBox>
        </>
      )}
    </FormikWrapper>
  );
};

export default ApplicationDetails;
